import React, { useEffect, useState, useRef } from 'react';
import { format, addDays } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getDoctorById, getDoctorNameListByPatient } from '../redux/doctorSlice';
import CommonLoader from '../components/CommonLoader';
import { getPatientById } from '../redux/patientSlice';
import { getAvailableSlots, rescheduleAppointment, getRescheduleRecommendation, cancelAppointment, getAvailableDoctor, getSingleDaySlots } from '../redux/appointmentSlice';
import { getSingleCustomPrice } from '../redux/customPriceSlice';
import { getallspecilitylist, getSpCategoryList } from '../redux/masterSlice';
import { isFeatureEnabled, FEATURE_KEYS } from '../redux/featureSlice';
import Pagination from '../components/Pagination';
import CustomDatePicker from '../components/dashboard/CustomDatePicker';
import calender_empty from '../assets/img/calender_emptty.svg';
import noslotts from '../assets/img/colorkit (1).svg';
import errorimg from '../assets/img/colorkit (1).svg';
import ErrorPage from '../components/ErrorPage';
import PatientAppointmentModal from '../components/modals/PatientAppointmentModal';
import MyDoctorMode from '../components/patientAppointment/MyDoctorMode';
import AllDoctorMode from '../components/patientAppointment/AllDoctorMode';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, CalendarCheck, CalendarX, MessageSquare, Clock, Euro, IndianRupee, User, Mail, Phone, Award, Bell, X, Search, ChevronDown, ChevronUp } from 'lucide-react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import MasterLabel from '../components/common/MasterLabel';
import { extractErrorMessage } from '../utils/extractErrorMessage';

// Debounce hook
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};

export default function PatientAppointment() {
    const { t, i18n } = useTranslation('patientappointment');
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const [selectedDoctorId, setSelectedDoctorId] = useState('');
    const [selectedSpeciality, setSelectedSpeciality] = useState('');
    const [selectedAddressId, setSelectedAddressId] = useState('');
    const [selectedAppointmentType, setSelectedAppointmentType] = useState('');
    const [weekOffset, setWeekOffset] = useState(0);
    const [openDay, setOpenDay] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [isRescheduling, setIsRescheduling] = useState(false);
    const [rescheduleData, setRescheduleData] = useState(null);
    const [rescheduleRecommendations, setRescheduleRecommendations] = useState([]);
    const [isRecommendationModalOpen, setIsRecommendationModalOpen] = useState(false);
    const [selectedRecommendation, setSelectedRecommendation] = useState(null);
    const [isViewingMoreSlots, setIsViewingMoreSlots] = useState(false);
    const [weekOffsetMore, setWeekOffsetMore] = useState(0);
    const [openDayMore, setOpenDayMore] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [appointmentMode, setAppointmentMode] = useState('myDoctor'); // 'myDoctor' or 'allDoctor'
    const [selectedSpecialtyId, setSelectedSpecialtyId] = useState('');
    const [selectedSpecialty, setSelectedSpecialty] = useState(null);
    const [selectedDate, setSelectedDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
    const categoryDropdownRef = useRef(null);
    const [isSpecialtyDropdownOpen, setIsSpecialtyDropdownOpen] = useState(false);
    const [specialtySearchQuery, setSpecialtySearchQuery] = useState('');
    const [specialtySearchError, setSpecialtySearchError] = useState('');
    const specialtyDropdownRef = useRef(null);
    const specialtySearchInputRef = useRef(null);
    const [openDoctorDrawer, setOpenDoctorDrawer] = useState(null); // Track which doctor card has drawer open
    const [doctorSelections, setDoctorSelections] = useState({}); // Store selections per doctor: { doctorId: { appointmentType: '', address: '' } }
    const [selectedDoctorForSlots, setSelectedDoctorForSlots] = useState(null); // Store selected doctor info when viewing slots in All Doctor mode

    const MAX_SPECIALTY_SEARCH_LENGTH = 50;
    const debouncedSpecialtySearch = useDebounce(specialtySearchQuery, 400);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { selectedPatient, isLoading, error } = useSelector((state) => state.patient);
    const { selectedDoctor, loading, error: error1, doctorNameListByPatient, loadingList } = useSelector((state) => state.doctor);
    const { availableSlots, availableDoctorSlots, isLoading: appointmentLoading, availableDoctors, availableDoctorsMetadata, isAvailableDoctorsLoading, singleDaySlots, isSingleDaySlotsLoading } = useSelector((state) => state.appointment);
    const { selectedCustomPrice } = useSelector((state) => state.customPrice);
    const { specialties, specialtyLoading, spCategoryList, spCategoryListLoading } = useSelector((state) => state.master);
    // Admin Settings → Patient → All Doctor Mode controls whether the
    // "All Doctors" radio is offered alongside "My Doctors".
    const allDoctorModeEnabled = useSelector((state) => isFeatureEnabled(state, FEATURE_KEYS.patientAllDoctorMode));
    const Id = localStorage.getItem('ID');
    const isInitialCall = useRef(true);
    const hasFetchedRecommendations = useRef(false);
    const recommendationModalRef = useRef(null);
    const isReschedulingFromState = useRef(false);
    const rescheduleFieldsSet = useRef(false);
    const isFromAllDoctorMode = useRef(false);

    // Helper function to get the Monday of the current week
    const getCurrentWeekMonday = (date) => {
        const day = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const diff = date.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(date.getFullYear(), date.getMonth(), diff);
    };

    // Helper function to scroll to bottom of modal
    const scrollRecommendationModalToBottom = () => {
        setTimeout(() => {
            if (recommendationModalRef.current) {
                recommendationModalRef.current.scrollTop = recommendationModalRef.current.scrollHeight;
            }
        }, 0);
    };

    // Handle View More Slots button click
    const handleViewMoreSlots = (recommendation) => {
        setIsViewingMoreSlots(true);
        setSelectedRecommendation(recommendation);
        setWeekOffsetMore(0);
        setOpenDayMore(null);

        // Fetch available slots
        const currentWeekMonday = getCurrentWeekMonday(today);
        const startDate = format(addDays(currentWeekMonday, 0), 'yyyy-MM-dd');
        const endDate = format(addDays(new Date(startDate), 6), 'yyyy-MM-dd');

        dispatch(getAvailableSlots({
            doctorId: recommendation.doctorId,
            startDate,
            endDate,
            timeZone: systemTimeZone,
            addressType: recommendation.addressType,
            appointmentTypeId: recommendation.appointmentType,
            duration: recommendation.duration,
            isReschedulingProcess: true,
            appointmentId: recommendation.appointmentId,
        }));

        // Scroll to bottom to show available slots
        scrollRecommendationModalToBottom();
    };

    // Handle direct slot click for rescheduling
    const handleDirectSlotClick = async (slot, recommendation) => {
        const slotDateTime = new Date(slot.start);

        // Check if the selected slot is in the past
        if (slotDateTime <= new Date()) {
            toast.warn(t('patientappointment.slot_passed_text'));
            return;
        }

        const result = await Swal.fire({
            title: t('patientappointment.confirm_reschedule_title'),
            text: t('patientappointment.confirm_reschedule_text', { date: format(slotDateTime, 'MMM d, yyyy h:mm a') }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('patientappointment.yes_reschedule_button'),
            cancelButtonText: t('patientappointment.cancel_button')
        });

        if (result.isConfirmed) {
            try {
                const date = format(slotDateTime, 'yyyy-MM-dd');
                const time = format(slotDateTime, 'HH:mm');

                // Calculate end time based on duration from the recommendation (original appointment)
                const selectedApptType = selectedDoctor?.appointmentSpeciality
                    ?.find(spec => spec.name === recommendation.specialtyName)
                    ?.appointmentTypes?.find(type => type.type === recommendation.specialtyTypeName || type.type === recommendation.appointTypeName);

                const duration = recommendation.duration || selectedApptType?.duration || '1:00';
                const durationStr = typeof duration === 'number' ? `${Math.floor(duration / 60)}:${duration % 60}` : String(duration);
                const [hours, minutes] = durationStr.split(':').map(Number);
                const slotEndTime = new Date(slotDateTime.getTime() + (hours * 60 + minutes) * 60 * 1000);
                const slotEndTimeStr = format(slotEndTime, 'HH:mm');

                const payload = {
                    appointmentId: recommendation.appointmentId,
                    patientId: selectedPatient?.patientId || Id,
                    doctorId: recommendation.doctorId,
                    newDate: date,
                    newTime: time,
                    newSlotEndTime: slotEndTimeStr,
                    timeZone: systemTimeZone,
                    appointmentTypeId: selectedApptType?.typeId || recommendation.appointmentType,
                    description: recommendation.description || '',
                    doctorDescription: recommendation.doctorDescription || null,
                    appointmentMode: recommendation.appointmentMode || 'onsite',
                    SpecialtyId: selectedApptType?.specialtyId || recommendation.SpecialtyId,
                    addressType: recommendation.addressType,
                    duration: durationStr,
                };

                const actionResult = await dispatch(rescheduleAppointment(payload));

                if (actionResult.meta.requestStatus === 'fulfilled') {
                    // Remove the rescheduled appointment from local state immediately
                    setRescheduleRecommendations(prev =>
                        prev.filter(rec => rec.appointmentId !== recommendation.appointmentId)
                    );

                    setIsRecommendationModalOpen(false);
                    setIsViewingMoreSlots(false);
                    setSelectedRecommendation(null);

                    toast.success(t('patientappointment.reschedule_success_text'));

                    // Navigate to MyAppointment page
                    navigate('/myAppointment', { replace: true });
                } else {
                    throw new Error(extractErrorMessage(actionResult.payload, t('patientappointment.reschedule_error_text'), i18n.language));
                }
            } catch (err) {
                toast.warn(extractErrorMessage(err, t('patientappointment.reschedule_error_text'), i18n.language));
            }
        }
    };

    // Handle week navigation
    const handleNextWeekMore = () => {
        setWeekOffsetMore(prev => Math.min(2, prev + 1));
        setOpenDayMore(null);
    };

    const handlePrevWeekMore = () => {
        setWeekOffsetMore(prev => Math.max(0, prev - 1));
        setOpenDayMore(null);
    };

    // Toggle day drawer
    const toggleDayDrawerMore = (day) => {
        setOpenDayMore(openDayMore === day ? null : day);
    };

    // Fetch slots when week offset changes (for week navigation in "View More Slots")
    useEffect(() => {
        if (isViewingMoreSlots && selectedRecommendation) {
            const currentWeekMonday = getCurrentWeekMonday(today);
            const startDate = format(addDays(currentWeekMonday, weekOffsetMore * 7), 'yyyy-MM-dd');
            const endDate = format(addDays(new Date(startDate), 6), 'yyyy-MM-dd');

            dispatch(getAvailableSlots({
                doctorId: selectedRecommendation.doctorId,
                startDate,
                endDate,
                timeZone: systemTimeZone,
                addressType: selectedRecommendation.addressType,
                appointmentTypeId: selectedRecommendation.appointmentType,
                duration: selectedRecommendation.duration,
                isReschedulingProcess: true,
                appointmentId: selectedRecommendation.appointmentId,
            }));
        }
    }, [weekOffsetMore, isViewingMoreSlots]);

    // Handle reschedule data from location state
    useEffect(() => {
        const rescheduleDataFromState = location.state?.reschedulestate;
        if (rescheduleDataFromState) {
            setIsRescheduling(true);
            setRescheduleData(rescheduleDataFromState);
            isReschedulingFromState.current = true;
            rescheduleFieldsSet.current = false;

            // Reset all fields first
            setSelectedSpeciality('');
            setSelectedAddressId('');
            setSelectedAppointmentType('');

            // Set doctor ID and fetch doctor data
            if (rescheduleDataFromState.doctorId) {
                setSelectedDoctorId(rescheduleDataFromState.doctorId);
                // Fetch doctor data - this will trigger the field population useEffect
                dispatch(getDoctorById(rescheduleDataFromState.doctorId));
            }
        }
    }, [location.state, dispatch]);

    useEffect(() => {
        if (isInitialCall.current) {
            if (Id) {
                dispatch(getPatientById(Id));
            }
            if (selectedPatient?.doctorId) {
                setSelectedDoctorId(selectedPatient.doctorId);
                dispatch(getDoctorById(selectedPatient.doctorId));
            }
            isInitialCall.current = false;
        }
    }, [dispatch, Id, selectedPatient?.doctorId]);

    // Fetch reschedule recommendations once on initial load
    useEffect(() => {
        if (Id && !hasFetchedRecommendations.current) {
            hasFetchedRecommendations.current = true;
            dispatch(getRescheduleRecommendation({
                patientId: Id,
                status: 'Rescheduling'
            })).then((result) => {
                if (result.meta.requestStatus === 'fulfilled') {
                    const recommendations = result.payload?.appointments || result.payload?.data?.appointments || [];
                    setRescheduleRecommendations(recommendations || []);
                }
            });
        }
    }, [dispatch, Id]);

    // Validation function for required fields
    const validateFields = () => {
        const errors = {};

        if (!selectedDoctorId) {
            errors.doctor = t('patientappointment.validation_doctor_error', 'Please select a doctor');
        }

        if (!selectedSpeciality) {
            errors.speciality = t('patientappointment.validation_speciality_error', 'Please select a speciality');
        }

        if (!selectedAddressId) {
            errors.address = t('patientappointment.validation_address_error', 'Please select an address');
        }

        if (!selectedAppointmentType) {
            errors.appointmentType = t('patientappointment.validation_appointment_type_error', 'Please select an appointment type');
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Clear errors when fields are selected
    useEffect(() => {
        if (selectedDoctorId && fieldErrors.doctor) {
            setFieldErrors(prev => ({ ...prev, doctor: '' }));
        }
    }, [selectedDoctorId, fieldErrors.doctor]);

    useEffect(() => {
        if (selectedSpeciality && fieldErrors.speciality) {
            setFieldErrors(prev => ({ ...prev, speciality: '' }));
        }
    }, [selectedSpeciality, fieldErrors.speciality]);

    useEffect(() => {
        if (selectedAddressId && fieldErrors.address) {
            setFieldErrors(prev => ({ ...prev, address: '' }));
        }
    }, [selectedAddressId, fieldErrors.address]);

    useEffect(() => {
        if (selectedAppointmentType && fieldErrors.appointmentType) {
            setFieldErrors(prev => ({ ...prev, appointmentType: '' }));
        }
    }, [selectedAppointmentType, fieldErrors.appointmentType]);

    useEffect(() => {
        if (!isInitialCall.current && selectedDoctor == null) {
            if (selectedPatient?.doctorId) {
                dispatch(getDoctorById(selectedPatient.doctorId));
            }
        }
    }, [dispatch, selectedPatient?.doctorId]);

    // Fetch doctors list when patient is available
    useEffect(() => {
        const patientId = selectedPatient?.patientId || Id;
        if (patientId) {
            dispatch(getDoctorNameListByPatient(patientId));
        }
    }, [dispatch, selectedPatient?.patientId, Id]);

    // Fetch category list on mount
    useEffect(() => {
        dispatch(getSpCategoryList());
    }, [dispatch]);

    // Fetch specialty list when dropdown is open and search changes (filtered by selected category)
    useEffect(() => {
        if (isSpecialtyDropdownOpen && !specialtySearchError) {
            const trimmedSearch = debouncedSpecialtySearch.trim();
            dispatch(getallspecilitylist({ search: trimmedSearch, categoryId: selectedCategory?.spcategoryId || '' }));
        }
    }, [dispatch, debouncedSpecialtySearch, isSpecialtyDropdownOpen, specialtySearchError, selectedCategory]);

    // Close specialty and category dropdowns on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (specialtyDropdownRef.current && !specialtyDropdownRef.current.contains(event.target)) {
                setIsSpecialtyDropdownOpen(false);
                setSpecialtySearchQuery('');
            }
            if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
                setIsCategoryDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Fetch available doctors when specialty and date are selected in All Doctor mode
    useEffect(() => {
        if (appointmentMode === 'allDoctor' && selectedSpecialtyId && selectedDate) {
            const patientId = selectedPatient?.patientId || Id;
            if (patientId) {
                dispatch(getAvailableDoctor({
                    specialtyId: selectedSpecialtyId,
                    startDate: selectedDate,
                    patientId: patientId,
                    page: currentPage,
                    limit: itemsPerPage
                }));
            }
        }
    }, [dispatch, appointmentMode, selectedSpecialtyId, selectedDate, currentPage, itemsPerPage, selectedPatient?.patientId, Id]);

    // Call getDoctorById when doctor is selected
    useEffect(() => {
        if (selectedDoctorId && !isReschedulingFromState.current && !isFromAllDoctorMode.current) {
            dispatch(getDoctorById(selectedDoctorId));
            // Reset dependent fields when doctor changes (only if not rescheduling and not from All Doctor mode)
            setSelectedSpeciality('');
            setSelectedAddressId('');
            setSelectedAppointmentType('');
            setWeekOffset(0);
            setOpenDay(null);
        } else if (selectedDoctorId && isFromAllDoctorMode.current) {
            // If coming from All Doctor mode, just fetch doctor data without resetting fields
            dispatch(getDoctorById(selectedDoctorId));
            isFromAllDoctorMode.current = false; // Reset the flag
        }
    }, [dispatch, selectedDoctorId]);

    // Populate all fields from reschedule data after doctor data is fully loaded
    useEffect(() => {
        if (isReschedulingFromState.current && rescheduleData && selectedDoctor && !loading && !rescheduleFieldsSet.current) {
            // Ensure doctor data is complete (has address and appointmentSpeciality arrays)
            if (selectedDoctor.address && selectedDoctor.appointmentSpeciality) {
                // Step 1: Set specialty first (required for address filtering)
                if (rescheduleData.specialtyName) {
                    const specialtyExists = selectedDoctor.appointmentSpeciality.some(
                        spec => spec.name === rescheduleData.specialtyName
                    );
                    if (specialtyExists) {
                        setSelectedSpeciality(rescheduleData.specialtyName);
                    }
                }

                // Step 2: Set appointment type (can be set independently)
                if (rescheduleData.appointTypeName || rescheduleData.specialtyTypeName) {
                    const appointmentTypeName = rescheduleData.appointTypeName || rescheduleData.specialtyTypeName;
                    // Verify appointment type exists for the specialty
                    const specialty = selectedDoctor.appointmentSpeciality.find(
                        spec => spec.name === rescheduleData.specialtyName
                    );
                    if (specialty) {
                        const appointmentTypeExists = specialty.appointmentTypes?.some(
                            type => type.type === appointmentTypeName
                        );
                        if (appointmentTypeExists) {
                            setSelectedAppointmentType(appointmentTypeName);
                        }
                    }
                }

                // Mark fields as set (address will be set in next useEffect after specialty is set)
                rescheduleFieldsSet.current = true;
            }
        }
    }, [selectedDoctor, rescheduleData, loading]);

    // Populate address field after specialty is set (addresses are filtered by specialty)
    useEffect(() => {
        if (isReschedulingFromState.current && rescheduleData && selectedDoctor && selectedSpeciality && rescheduleFieldsSet.current && !loading) {
            // Get addresses filtered by specialty (same logic as in the component)
            const addressesForSpecialty = selectedDoctor?.address?.filter(addr =>
                addr.specialty?.some(spec => spec.name === selectedSpeciality)
            ) || [];

            if (rescheduleData.addressType && addressesForSpecialty.length > 0) {
                // First, try to find if it's already an address ID
                const addressById = addressesForSpecialty.find(addr =>
                    addr.addressId === rescheduleData.addressType
                );
                if (addressById) {
                    setSelectedAddressId(rescheduleData.addressType);
                } else {
                    // If not found by ID, try to find by address type string
                    const addressByType = addressesForSpecialty.find(addr =>
                        addr.addressType === rescheduleData.addressType
                    );
                    if (addressByType) {
                        setSelectedAddressId(addressByType.addressId);
                    } else if (addressesForSpecialty.length > 0) {
                        // Fallback: use the first available address for this specialty
                        setSelectedAddressId(addressesForSpecialty[0].addressId);
                    }
                }
            } else if (addressesForSpecialty.length > 0 && !selectedAddressId) {
                // If no addressType in reschedule data but addresses are available, use first one
                setSelectedAddressId(addressesForSpecialty[0].addressId);
            }
        }
    }, [selectedDoctor, selectedSpeciality, rescheduleData, loading, selectedAddressId]);

    // Call getAvailableSlots when rescheduleData or input fields change
    useEffect(() => {
        if (selectedDoctorId && selectedSpeciality && selectedAddressId && selectedAppointmentType) {
            // Clear any validation errors when all fields are selected
            setFieldErrors({});

            // Get the Monday of the current week
            const currentWeekMonday = getCurrentWeekMonday(today);

            // Calculate start date (Monday of the target week)
            const startDate = format(addDays(currentWeekMonday, weekOffset * 7), 'yyyy-MM-dd');

            // Calculate end date (Sunday of the target week - 6 days after Monday)
            const endDate = format(addDays(new Date(startDate), 6), 'yyyy-MM-dd');

            if (appointmentMode === 'myDoctor') {
                dispatch(getAvailableSlots({
                    doctorId: selectedDoctorId,
                    startDate,
                    endDate,
                    timeZone: systemTimeZone,
                    addressType: selectedAddressId,
                    appointmentTypeId: selectedDoctor?.appointmentSpeciality.find(
                        spec => spec.name === selectedSpeciality
                    )?.appointmentTypes.find(type => type.type === selectedAppointmentType)?.typeId || 'AT00023',
                    duration: rescheduleData?.duration,
                    isReschedulingProcess: true,
                }));
            }
        }
    }, [dispatch, selectedDoctorId, selectedSpeciality, selectedAddressId, selectedAppointmentType, selectedDoctor, weekOffset]);

    // Call getSingleCustomPrice when appointment type is selected
    useEffect(() => {
        if (selectedDoctorId && selectedSpeciality && selectedAppointmentType && selectedPatient && selectedDoctor) {
            const patientId = selectedPatient?.patientId || Id;
            const selectedApptDetails = selectedDoctor?.appointmentSpeciality
                ?.find(spec => spec.name === selectedSpeciality)
                ?.appointmentTypes?.find(type => type.type === selectedAppointmentType);

            const specialityId = selectedApptDetails?.specialtyId;
            const appointmentTypeId = selectedApptDetails?.typeId;

            if (patientId && selectedDoctorId && specialityId && appointmentTypeId) {
                dispatch(getSingleCustomPrice({
                    patientId,
                    doctorId: selectedDoctorId,
                    specialityId,
                    appointmentTypeId
                }));
            }
        }
    }, [dispatch, selectedDoctorId, selectedSpeciality, selectedAppointmentType, selectedPatient, selectedDoctor, Id]);

    const specialities = selectedDoctor?.appointmentSpeciality
        ?.filter(spec => spec.appointmentTypes && spec.appointmentTypes.length > 0)
        ?.map(spec => spec.name) || [];
    const addressTypes = selectedDoctor?.address?.filter(addr =>
        addr.specialty.some(spec => spec.name === selectedSpeciality)
    ).map(addr => ({
        id: addr.addressId,
        type: addr.addressType
    })) || [];
    const appointmentTypes = selectedDoctor?.appointmentSpeciality?.find(
        spec => spec.name === selectedSpeciality
    )?.appointmentTypes?.map(type => type.type) || [];

    const selectedAppointmentDetails = selectedDoctor?.appointmentSpeciality
        ?.find(spec => spec.name === selectedSpeciality)
        ?.appointmentTypes?.find(type => type.type === selectedAppointmentType);

    const selectedAddressCountry = selectedDoctor?.address?.find(
        addr => addr.addressId === selectedAddressId
    )?.country;

    const availableDays = Object.entries(availableSlots || {})
        .map(([day, data]) => ({ day, ...data }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));

    const handleNextWeek = () => {
        setWeekOffset(prev => Math.min(2, prev + 1));
        setOpenDay(null);
    };

    const handlePrevWeek = () => {
        setWeekOffset(prev => Math.max(0, prev - 1));
        setOpenDay(null);
    };

    const toggleDayDrawer = (day) => {
        setOpenDay(openDay === day ? null : day);
    };

    const handleSlotClick = (slot) => {
        // Validate all fields before allowing slot selection
        if (!validateFields()) {
            return;
        }

        const dur = selectedAppointmentDetails?.duration;
        let durationMs = 60 * 60 * 1000; // default 1 hour
        if (dur) {
            if (typeof dur === 'number') {
                durationMs = dur * 60 * 1000;
            } else {
                const durStr = String(dur);
                if (durStr.includes(':')) {
                    durationMs = parseInt(durStr.split(':')[0]) * 60 * 60 * 1000 + parseInt(durStr.split(':')[1]) * 60 * 1000;
                } else {
                    durationMs = parseInt(durStr) * 60 * 1000;
                }
            }
        }
        setSelectedSlot({
            start: slot.start,
            end: new Date(new Date(slot.start).getTime() + durationMs).toISOString()
        });
        setIsModalOpen(true);
    };

    // Handle reschedule confirmation
    const handleRescheduleConfirm = () => {
        setIsRescheduling(false);
        setRescheduleData(null);
        isReschedulingFromState.current = false;
        rescheduleFieldsSet.current = false;
        setIsModalOpen(false);
        setSelectedSlot(null);
        navigate('/myAppointment', { replace: true });
    };

    // Handle appointment booked callback - navigate to MyAppointment page
    const handleAppointmentBooked = () => {
        setIsModalOpen(false);
        setSelectedSlot(null);

        // If in All Doctor mode, close drawer and reset fields after successful booking
        if (appointmentMode === 'allDoctor') {
            setOpenDoctorDrawer(null);
            setSelectedDoctorForSlots(null);
            // Clear selections for all doctors
            setDoctorSelections({});
        }

        navigate('/myAppointment', { replace: true });
    };

    // Handle abort rescheduling
    const handleAbortReschedule = async () => {
        const result = await Swal.fire({
            title: t('patientappointment.abort_reschedule_title') || 'Abort Rescheduling?',
            text: t('patientappointment.abort_reschedule_text') || 'Are you sure you want to abort the rescheduling process? All changes will be lost.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#4ABDBD',
            confirmButtonText: t('patientappointment.yes_abort_button') || 'Yes, Abort',
            cancelButtonText: t('patientappointment.cancel_button') || 'Cancel'
        });

        if (result.isConfirmed) {
            setIsRescheduling(false);
            setRescheduleData(null);
            isReschedulingFromState.current = false;
            rescheduleFieldsSet.current = false;
            setSelectedDoctorId('');
            setSelectedSpeciality('');
            setSelectedAddressId('');
            setSelectedAppointmentType('');
            setWeekOffset(0);
            setOpenDay(null);
            setSelectedSlot(null);
            setIsModalOpen(false);
            navigate('/patientAppointment', { replace: true });
        }
    };

    const formatDurationToMinutes = (timeStr) => {
        if (!timeStr || !timeStr.includes(":")) return timeStr;
        const [hours, minutes] = timeStr.split(":").map(Number);
        const totalMinutes = (hours * 60) + minutes;
        return `${totalMinutes} mins`;
    };

    // Validate specialty search input
    const validateSpecialtySearchInput = (value) => {
        const sanitized = value.replace(/[<>]/g, '');

        if (sanitized.length > MAX_SPECIALTY_SEARCH_LENGTH) {
            setSpecialtySearchError(t('patientappointment.search_exceed_limit', { limit: MAX_SPECIALTY_SEARCH_LENGTH }));
            return false;
        }

        if (sanitized.trim().length === 0 && sanitized.length > 0) {
            setSpecialtySearchError(t('patientappointment.search_only_spaces'));
            return false;
        }

        setSpecialtySearchError('');
        return true;
    };

    // Handle specialty search change
    const handleSpecialtySearchChange = (e) => {
        const value = e.target.value;

        if (specialtySearchError && value.length < specialtySearchQuery.length) {
            setSpecialtySearchError('');
        }

        if (validateSpecialtySearchInput(value)) {
            const sanitized = value.replace(/[<>]/g, '');
            setSpecialtySearchQuery(sanitized);
        }
    };

    // Handle category selection
    const handleCategorySelect = (category) => {
        setSelectedCategory(category);
        setIsCategoryDropdownOpen(false);
        // Reset specialty when category changes
        setSelectedSpecialty(null);
        setSelectedSpecialtyId('');
        setSpecialtySearchQuery('');
        setCurrentPage(1);
        // Pre-fetch specialties for the selected category
        if (category) {
            dispatch(getallspecilitylist({ categoryId: category.spcategoryId }));
        }
    };

    // Handle specialty selection
    const handleSpecialtySelect = (specialty) => {
        if (specialty) {
            setSelectedSpecialty(specialty);
            setSelectedSpecialtyId(specialty.specialtyId || specialty._id);
        } else {
            setSelectedSpecialty(null);
            setSelectedSpecialtyId('');
        }
        setIsSpecialtyDropdownOpen(false);
        setSpecialtySearchQuery('');
        setCurrentPage(1);
    };

    // Calculate display dates for the week header
    const currentWeekMonday = getCurrentWeekMonday(today);
    const startDate = addDays(currentWeekMonday, weekOffset * 7);
    const endDate = addDays(startDate, 6);

    if (isLoading || loading) {
        return (
            <div className="bg-white rounded-2xl p-4 sm:p-6 w-full min-h-screen flex items-center justify-center">
                <div className="flex flex-col items-center justify-center py-4">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD] font-semibold">{t('patientappointment.loading')}</span>
                </div>
            </div>
        );
    }

    if ((error?.status >= 500 && error?.status <= 599) || (error1?.status >= 500 && error1?.status <= 599) || error?.message === 'Network Error' || error1?.message === 'Network Error') {
        return <ErrorPage error={error} pageName={t('patientappointment.title')} />;
    }

    // Check if patient profile is incomplete - restrict booking appointments
    if (selectedPatient && selectedPatient.profileStatus === 'incomplete' && !isLoading) {
        return (
            <div className="h-full bg-white rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 flex items-center justify-center">
                <div className="text-center max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl">
                    <img
                        src={errorimg}
                        alt={t('patientappointment.profile_incomplete_alt', 'Profile Incomplete')}
                        className="mx-auto w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 xl:w-64 xl:h-64 object-contain"
                    />
                    <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#68c0c0] mb-3 sm:mb-4">
                        {t('patientappointment.unable_to_book', 'Unable to Book Appointment')}
                    </h2>
                    <p className="text-sm sm:text-base md:text-lg text-gray-800 mb-2 sm:mb-3 md:mb-4">
                        {t('patientappointment.profile_incomplete_message', 'Your profile is incomplete. Please complete your profile to book appointments.')}
                    </p>
                    <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-4 sm:mb-6 md:mb-8">
                        {t('patientappointment.update_profile_message', 'Please update your profile information to continue.')}
                    </p>
                    <button
                        onClick={() => navigate('/patientProfile')}
                        className="px-3 py-1.5 sm:px-4 sm:py-2 md:px-5 md:py-2.5 cursor-pointer bg-[#4ABDBD] text-white font-semibold rounded-lg shadow-sm text-xs sm:text-sm md:text-base transition-all hover:bg-[#3aa8a8] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:ring-opacity-50"
                        data-testid="patient-appointment-go-to-profile-button"
                    >
                        {t('patientappointment.go_to_profile', 'Go to Profile')}
                    </button>
                </div>
            </div>
        );
    }

    // if (!selectedDoctor || !selectedPatient) {
    //     return (
    //         <ErrorPage error='something went wrong' pageName={t('patientappointment.title')} />
    //     );
    // }

    // Handle opening recommendation modal
    const handleOpenRecommendationModal = () => {
        setIsRecommendationModalOpen(true);
    };

    // Handle slot selection from recommendation
    const handleRecommendationSlotClick = async (slot, recommendation) => {
        const slotDateTime = new Date(slot.start);

        // Check if the selected slot is in the past
        if (slotDateTime <= new Date()) {
            toast.warn(t('patientappointment.slot_passed_text'));
            return;
        }

        const result = await Swal.fire({
            title: t('patientappointment.confirm_reschedule_title'),
            text: t('patientappointment.confirm_reschedule_text', { date: format(slotDateTime, 'MMM d, yyyy h:mm a') }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('patientappointment.yes_reschedule_button'),
            cancelButtonText: t('patientappointment.cancel_button')
        });

        if (result.isConfirmed) {
            try {
                const selectedApptType = selectedDoctor?.appointmentSpeciality
                    ?.find(spec => spec.name === recommendation.specialtyName)
                    ?.appointmentTypes?.find(type => type.type === recommendation.appointTypeName);

                const slotDate = new Date(slot.start);
                const date = format(slotDate, 'yyyy-MM-dd');
                const time = format(slotDate, 'HH:mm');

                // Calculate end time based on duration from the recommendation (original appointment)
                const duration = recommendation.duration || selectedApptType?.duration || '1:00';
                const durationStr2 = typeof duration === 'number' ? `${Math.floor(duration / 60)}:${duration % 60}` : String(duration);
                const [hours, minutes] = durationStr2.split(':').map(Number);
                const slotEndTime = new Date(slotDate.getTime() + (hours * 60 + minutes) * 60 * 1000);
                const slotEndTimeStr = format(slotEndTime, 'HH:mm');

                const payload = {
                    appointmentId: recommendation.appointmentId,
                    patientId: Id,
                    doctorId: recommendation.doctorId,
                    newDate: date,
                    newTime: time,
                    newSlotEndTime: slotEndTimeStr,
                    timeZone: systemTimeZone,
                    appointmentTypeId: selectedApptType?.typeId || recommendation.appointmentType,
                    description: recommendation.description || '',
                    doctorDescription: recommendation.doctorDescription || null,
                    appointmentMode: recommendation.appointmentMode || 'onsite',
                    SpecialtyId: selectedApptType?.specialtyId || recommendation.SpecialtyId,
                    addressType: recommendation.addressType,
                    duration: durationStr2,
                };

                const actionResult = await dispatch(rescheduleAppointment(payload));

                if (actionResult.meta.requestStatus === 'fulfilled') {
                    // Remove the recommendation from the list
                    setRescheduleRecommendations(prev =>
                        prev.filter(rec => rec.appointmentId !== recommendation.appointmentId)
                    );
                    setIsRecommendationModalOpen(false);
                    setSelectedRecommendation(null);

                    toast.success(t('patientappointment.reschedule_success_text'));

                    // Navigate to MyAppointment page
                    navigate('/myAppointment', { replace: true });
                } else {
                    throw new Error(extractErrorMessage(actionResult.payload, t('patientappointment.reschedule_error_text'), i18n.language));
                }
            } catch (err) {
                toast.warn(extractErrorMessage(err, t('patientappointment.reschedule_error_text'), i18n.language));
            }
        }
    };

    // Handle cancel appointment from recommendation
    const handleCancelRecommendationAppointment = async (recommendation) => {
        const result = await Swal.fire({
            title: t('patientappointment.cancel_appointment_confirm_title'),
            text: t('patientappointment.cancel_appointment_confirm_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#4ABDBD',
            confirmButtonText: t('patientappointment.yes_cancel_button'),
            cancelButtonText: t('patientappointment.no_button')
        });

        if (result.isConfirmed) {
            try {
                const actionResult = await dispatch(cancelAppointment({
                    appointmentId: recommendation.appointmentId,
                    doctorId: recommendation.doctorId
                }));

                if (actionResult.meta.requestStatus === 'fulfilled') {
                    // Remove the recommendation from the list
                    setRescheduleRecommendations(prev =>
                        prev.filter(rec => rec.appointmentId !== recommendation.appointmentId)
                    );
                    setIsRecommendationModalOpen(false);
                    setSelectedRecommendation(null);

                    toast.success(t('patientappointment.cancel_success_text'));
                } else {
                    throw new Error(extractErrorMessage(actionResult.payload, t('patientappointment.cancel_error_text'), i18n.language));
                }
            } catch (err) {
                toast.warn(extractErrorMessage(err, t('patientappointment.cancel_error_text'), i18n.language));
            }
        }
    };

    return (
        <div data-testid="patient-appointment-page" className="bg-white rounded-2xl p-4 shadow-md sm:p-6  w-full mx-auto relative">
            <div data-testid="patient-appointment-content">
                {isRescheduling && (
                    <div data-testid="rescheduling-banner" className="bg-red-500 text-white p-3 rounded-md mb-4 flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            {loading && (
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            )}
                            <span data-testid="rescheduling-message">
                                {loading ? (t('patientappointment.loading_reschedule_data') || 'Loading appointment data...') : t('patientappointment.rescheduling_message')}
                            </span>
                        </div>
                        <button
                            data-testid="abort-reschedule-button"
                            onClick={handleAbortReschedule}
                            className="text-white font-bold hover:text-gray-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label={t('patientappointment.abort_reschedule_button')}
                            disabled={loading}
                        >
                            X
                        </button>
                    </div>
                )}
                {!isRescheduling && (<div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-4">
                        <h2 data-testid="patient-appointment-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">{t('patientappointment.title')}</h2>
                        {/* Mode Selection Radio Buttons — gated by admin
                            Settings → Patient → All Doctor Mode. When the
                            flag is off the patient sees no radios and stays
                            in the default 'myDoctor' mode. */}
                        {allDoctorModeEnabled && (
                            <div data-testid="patient-appointment-mode-selector" className="flex items-center gap-4 ml-4">
                                <label data-testid="patient-appointment-mode-my-doctor-label" className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        data-testid="patient-appointment-mode-radio-myDoctor"
                                        type="radio"
                                        name="appointmentMode"
                                        value="myDoctor"
                                        checked={appointmentMode === 'myDoctor'}
                                        onChange={(e) => {
                                            // Reset all fields when switching to My Doctor mode
                                            setAppointmentMode(e.target.value);
                                            // Reset All Doctor mode fields
                                            setSelectedSpecialtyId('');
                                            setSelectedSpecialty(null);
                                            setSelectedDate('');
                                            setCurrentPage(1);
                                            setIsSpecialtyDropdownOpen(false);
                                            setSpecialtySearchQuery('');
                                            setOpenDoctorDrawer(null);
                                            setDoctorSelections({});
                                            setSelectedDoctorForSlots(null);
                                            // Reset My Doctor mode fields
                                            setSelectedDoctorId('');
                                            setSelectedSpeciality('');
                                            setSelectedAddressId('');
                                            setSelectedAppointmentType('');
                                            setWeekOffset(0);
                                            setOpenDay(null);
                                            setSelectedSlot(null);
                                            setIsModalOpen(false);
                                        }}
                                        className="w-4 h-4 text-[#4ABDBD] focus:ring-[#4ABDBD] cursor-pointer"
                                    />
                                    <span data-testid="patient-appointment-mode-my-doctor-text" className="text-sm font-medium text-gray-700">{t('patientappointment.my_doctors', 'My Doctors')}</span>
                                </label>
                                <label data-testid="patient-appointment-mode-all-doctor-label" className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        data-testid="patient-appointment-mode-radio-allDoctor"
                                        type="radio"
                                        name="appointmentMode"
                                        value="allDoctor"
                                        checked={appointmentMode === 'allDoctor'}
                                        onChange={(e) => {
                                            // Reset all fields when switching to All Doctor mode
                                            setAppointmentMode(e.target.value);
                                            // Reset My Doctor mode fields
                                            setSelectedDoctorId('');
                                            setSelectedSpeciality('');
                                            setSelectedAddressId('');
                                            setSelectedAppointmentType('');
                                            setWeekOffset(0);
                                            setOpenDay(null);
                                            setSelectedSlot(null);
                                            setIsModalOpen(false);
                                            // Reset All Doctor mode fields
                                            setSelectedSpecialtyId('');
                                            setSelectedSpecialty(null);
                                            setSelectedDate('');
                                            setCurrentPage(1);
                                            setIsSpecialtyDropdownOpen(false);
                                            setSpecialtySearchQuery('');
                                            setOpenDoctorDrawer(null);
                                            setDoctorSelections({});
                                            setSelectedDoctorForSlots(null);
                                        }}
                                        className="w-4 h-4 text-[#4ABDBD] focus:ring-[#4ABDBD] cursor-pointer"
                                    />
                                    <span data-testid="patient-appointment-mode-all-doctor-text" className="text-sm font-medium text-gray-700">{t('patientappointment.all_doctors', 'All Doctors')}</span>
                                </label>
                            </div>
                        )}
                    </div>
                    {/* Reschedule Recommendation Button */}
                    <button
                        data-testid="patient-appointment-recommendations-button"
                        onClick={() => setIsRecommendationModalOpen(true)}
                        disabled={rescheduleRecommendations.length === 0}
                        className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-2 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Bell data-testid="patient-appointment-recommendations-icon" size={20} />
                        <span data-testid="patient-appointment-recommendations-text">{t('patientappointment.reschedule_recommendations_button')}</span>
                        {rescheduleRecommendations.length > 0 && (
                            <span data-testid="patient-appointment-recommendations-count" className="bg-white text-orange-500 rounded-full px-2 py-0.5 text-sm font-bold">
                                {rescheduleRecommendations.length}
                            </span>
                        )}
                    </button>
                </div>)}

                {/* Render Mode Components */}
                {appointmentMode === 'myDoctor' ? (
                    <MyDoctorMode
                        selectedDoctorId={selectedDoctorId}
                        selectedSpeciality={selectedSpeciality}
                        selectedAddressId={selectedAddressId}
                        selectedAppointmentType={selectedAppointmentType}
                        weekOffset={weekOffset}
                        openDay={openDay}
                        fieldErrors={fieldErrors}
                        isLoading={isLoading}
                        availableSlots={availableSlots}
                        selectedDoctor={selectedDoctor}
                        selectedAppointmentDetails={selectedAppointmentDetails}
                        setSelectedDoctorId={setSelectedDoctorId}
                        setSelectedSpeciality={setSelectedSpeciality}
                        setSelectedAddressId={setSelectedAddressId}
                        setSelectedAppointmentType={setSelectedAppointmentType}
                        setWeekOffset={setWeekOffset}
                        setOpenDay={setOpenDay}
                        handleSlotClick={handleSlotClick}
                        handlePrevWeek={handlePrevWeek}
                        handleNextWeek={handleNextWeek}
                        toggleDayDrawer={toggleDayDrawer}
                        formatDurationToMinutes={formatDurationToMinutes}
                        doctorNameListByPatient={doctorNameListByPatient}
                        loadingList={loadingList}
                        isRescheduling={isRescheduling}
                        loading={loading}
                        specialities={specialities}
                        addressTypes={addressTypes}
                        appointmentTypes={appointmentTypes}
                        today={today}
                        t={t}
                        setFieldErrors={setFieldErrors}
                    />
                ) : (
                    <AllDoctorMode
                        selectedCategory={selectedCategory}
                        isCategoryDropdownOpen={isCategoryDropdownOpen}
                        categoryDropdownRef={categoryDropdownRef}
                        spCategoryList={spCategoryList}
                        spCategoryListLoading={spCategoryListLoading}
                        setIsCategoryDropdownOpen={setIsCategoryDropdownOpen}
                        handleCategorySelect={handleCategorySelect}
                        selectedSpecialtyId={selectedSpecialtyId}
                        selectedSpecialty={selectedSpecialty}
                        selectedDate={selectedDate}
                        currentPage={currentPage}
                        itemsPerPage={itemsPerPage}
                        isSpecialtyDropdownOpen={isSpecialtyDropdownOpen}
                        specialtySearchQuery={specialtySearchQuery}
                        specialtySearchError={specialtySearchError}
                        specialtyDropdownRef={specialtyDropdownRef}
                        specialtySearchInputRef={specialtySearchInputRef}
                        openDoctorDrawer={openDoctorDrawer}
                        doctorSelections={doctorSelections}
                        selectedDoctorForSlots={selectedDoctorForSlots}
                        availableDoctors={availableDoctors}
                        availableDoctorsMetadata={availableDoctorsMetadata}
                        isAvailableDoctorsLoading={isAvailableDoctorsLoading}
                        singleDaySlots={singleDaySlots}
                        isSingleDaySlotsLoading={isSingleDaySlotsLoading}
                        specialties={specialties}
                        specialtyLoading={specialtyLoading}
                        today={today}
                        setSelectedSpecialtyId={setSelectedSpecialtyId}
                        setSelectedSpecialty={setSelectedSpecialty}
                        setSelectedDate={setSelectedDate}
                        setCurrentPage={setCurrentPage}
                        setItemsPerPage={setItemsPerPage}
                        setIsSpecialtyDropdownOpen={setIsSpecialtyDropdownOpen}
                        setSpecialtySearchQuery={setSpecialtySearchQuery}
                        setOpenDoctorDrawer={setOpenDoctorDrawer}
                        setDoctorSelections={setDoctorSelections}
                        setSelectedDoctorForSlots={setSelectedDoctorForSlots}
                        setSelectedSlot={setSelectedSlot}
                        setSelectedAddressId={setSelectedAddressId}
                        setSelectedAppointmentType={setSelectedAppointmentType}
                        setSelectedSpeciality={setSelectedSpeciality}
                        setSelectedDoctorId={setSelectedDoctorId}
                        setIsModalOpen={setIsModalOpen}
                        handleSpecialtySearchChange={handleSpecialtySearchChange}
                        handleSpecialtySelect={handleSpecialtySelect}
                        dispatch={dispatch}
                        getSingleDaySlots={getSingleDaySlots}
                        getDoctorById={getDoctorById}
                        getSingleCustomPrice={getSingleCustomPrice}
                        systemTimeZone={systemTimeZone}
                        selectedPatient={selectedPatient}
                        Id={Id}
                        isFromAllDoctorMode={isFromAllDoctorMode}
                        addDays={addDays}
                        t={t}
                        MAX_SPECIALTY_SEARCH_LENGTH={MAX_SPECIALTY_SEARCH_LENGTH}
                    />
                )}
            </div>
            <PatientAppointmentModal
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                selectedSlot={selectedSlot}
                setSelectedSlot={setSelectedSlot}
                selectedDoctor={selectedDoctor}
                selectedPatient={selectedPatient}
                selectedSpeciality={selectedSpeciality}
                selectedAddressId={selectedAddressId}
                selectedAppointmentType={selectedAppointmentType}
                isRescheduling={isRescheduling}
                onRescheduleConfirm={handleRescheduleConfirm}
                rescheduleData={rescheduleData}
                selectedCustomPrice={selectedCustomPrice}
                onAppointmentBooked={handleAppointmentBooked}
            />

            {/* Reschedule Recommendation Modal */}
            {isRecommendationModalOpen && (
                <div data-testid="reschedule-recommendation-modal" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
                    <div data-testid="reschedule-recommendation-modal-content" className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
                        {/* Header */}
                        <div data-testid="reschedule-recommendation-modal-header" className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
                            <h3 data-testid="reschedule-recommendation-modal-title" className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                                {t('patientappointment.reschedule_recommendations_modal_title')} ({rescheduleRecommendations.length})
                            </h3>
                            <button
                                data-testid="reschedule-recommendation-modal-close"
                                onClick={() => {
                                    setIsRecommendationModalOpen(false);
                                    setSelectedRecommendation(null);
                                }}
                                className="text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                            >
                                <X data-testid="reschedule-recommendation-modal-close-icon" className="h-5 w-5 sm:h-6 sm:w-6" />
                            </button>
                        </div>

                        {/* Content */}
                        <div data-testid="reschedule-recommendation-modal-body" ref={recommendationModalRef} className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
                            {rescheduleRecommendations.length === 0 ? (
                                <div data-testid="reschedule-recommendation-empty-state" className="text-center py-8 text-gray-500">
                                    <p data-testid="reschedule-recommendation-empty-text">{t('patientappointment.no_reschedule_recommendations_available')}</p>
                                </div>
                            ) : (
                                <div data-testid="reschedule-recommendation-list" className="space-y-6">
                                    {rescheduleRecommendations.map((recommendation) => (
                                        <div data-testid={`reschedule-recommendation-item-${recommendation.appointmentId || recommendation._id}`} key={recommendation.appointmentId || recommendation._id} className="border border-gray-200 rounded-xl p-4 sm:p-6 bg-gradient-to-br from-gray-50/50 to-white shadow-sm">
                                            {/* Appointment Details */}
                                            <div data-testid={`reschedule-recommendation-details-${recommendation.appointmentId || recommendation._id}`} className="mb-4 p-4 bg-gradient-to-br from-[#4ABDBD]/5 to-[#3A9B9B]/5 border border-[#4ABDBD]/20 rounded-xl">
                                                <h4 data-testid={`reschedule-recommendation-details-heading-${recommendation.appointmentId || recommendation._id}`} className="text-lg font-semibold text-gray-800 mb-3">{t('patientappointment.appointment_details_heading')}</h4>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                                    <div>
                                                        <span className="font-medium text-gray-600">{t('patientappointment.date_time_label')}:</span>
                                                        <p data-testid={`reschedule-recommendation-datetime-${recommendation.appointmentId || recommendation._id}`} className="text-gray-800">{format(new Date(recommendation.localTime), 'MMM d, yyyy h:mm a')}</p>
                                                    </div>
                                                    <div>
                                                        <span className="font-medium text-gray-600">{t('patientappointment.speciality_label')}:</span>
                                                        <p data-testid={`reschedule-recommendation-speciality-${recommendation.appointmentId || recommendation._id}`} className="text-gray-800">
                                                            <MasterLabel
                                                                type="masterSpecialty"
                                                                id={recommendation.SpecialtyId}
                                                                field="name"
                                                                fallback={recommendation.specialtyName || t('na')}
                                                            />
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <span className="font-medium text-gray-600">{t('patientappointment.appointment_type_label')}:</span>
                                                        <p data-testid={`reschedule-recommendation-appttype-${recommendation.appointmentId || recommendation._id}`} className="text-gray-800">{recommendation.specialtyTypeName || recommendation.appointTypeName || t('na')}</p>
                                                    </div>
                                                    <div>
                                                        <span className="font-medium text-gray-600">{t('patientappointment.status_label')}:</span>
                                                        <p data-testid={`reschedule-recommendation-status-${recommendation.appointmentId || recommendation._id}`} className="text-gray-800">{recommendation.status || t('na')}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Recommended Slots */}
                                            <div className="mb-4">
                                                <h4 className="text-lg font-semibold text-gray-800 mb-2">{t('patientappointment.select_recommended_slots_heading')}</h4>
                                                <p className="text-sm text-gray-600 mb-4">{t('patientappointment.reschedule_recommendation_message')}</p>
                                                <div className="flex flex-wrap items-center gap-3">
                                                    {recommendation.date_1 && (() => {
                                                        const slotDateTime = new Date(recommendation.date_1);
                                                        return (
                                                            <button
                                                                data-testid={`reschedule-recommendation-slot-1-${recommendation.appointmentId || recommendation._id}`}
                                                                onClick={() => handleRecommendationSlotClick({ start: slotDateTime.toISOString() }, recommendation)}
                                                                disabled={appointmentLoading}
                                                                className="px-4 py-2 bg-gradient-to-br from-[#4ABDBD]/10 to-[#3AA8A8]/10 border-2 border-[#4ABDBD] rounded-xl hover:bg-gradient-to-br hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                <div className="text-sm font-medium text-gray-800 hover:text-white">
                                                                    {format(slotDateTime, 'MMM d, yyyy h:mm a')}
                                                                </div>
                                                            </button>
                                                        );
                                                    })()}
                                                    {recommendation.date_2 && (() => {
                                                        const slotDateTime = new Date(recommendation.date_2);
                                                        return (
                                                            <button
                                                                data-testid={`reschedule-recommendation-slot-2-${recommendation.appointmentId || recommendation._id}`}
                                                                onClick={() => handleRecommendationSlotClick({ start: slotDateTime.toISOString() }, recommendation)}
                                                                disabled={appointmentLoading}
                                                                className="px-4 py-2 bg-gradient-to-br from-[#4ABDBD]/10 to-[#3AA8A8]/10 border-2 border-[#4ABDBD] rounded-xl hover:bg-gradient-to-br hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                <div className="text-sm font-medium text-gray-800 hover:text-white">
                                                                    {format(slotDateTime, 'MMM d, yyyy h:mm a')}
                                                                </div>
                                                            </button>
                                                        );
                                                    })()}
                                                    {recommendation.date_3 && (() => {
                                                        const slotDateTime = new Date(recommendation.date_3);
                                                        return (
                                                            <button
                                                                data-testid={`reschedule-recommendation-slot-3-${recommendation.appointmentId || recommendation._id}`}
                                                                onClick={() => handleRecommendationSlotClick({ start: slotDateTime.toISOString() }, recommendation)}
                                                                disabled={appointmentLoading}
                                                                className="px-4 py-2 bg-gradient-to-br from-[#4ABDBD]/10 to-[#3AA8A8]/10 border-2 border-[#4ABDBD] rounded-xl hover:bg-gradient-to-br hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                <div className="text-sm font-medium text-gray-800 hover:text-white">
                                                                    {format(slotDateTime, 'MMM d, yyyy h:mm a')}
                                                                </div>
                                                            </button>
                                                        );
                                                    })()}
                                                    <button
                                                        data-testid={`reschedule-recommendation-view-more-${recommendation.appointmentId || recommendation._id}`}
                                                        onClick={() => handleViewMoreSlots(recommendation)}
                                                        disabled={appointmentLoading}
                                                        className="px-4 py-2 bg-white border-2 border-gray-400 text-gray-600 rounded-full text-sm font-medium hover:bg-gray-100 transition-all duration-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {t('patientappointment.view_more_slots_button') || 'View More Slots'}
                                                    </button>
                                                    <button
                                                        data-testid={`reschedule-recommendation-cancel-${recommendation.appointmentId || recommendation._id}`}
                                                        onClick={() => handleCancelRecommendationAppointment(recommendation)}
                                                        disabled={appointmentLoading}
                                                        className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ml-auto"
                                                    >
                                                        {appointmentLoading && (
                                                            <span data-testid={`reschedule-recommendation-cancel-spinner-${recommendation.appointmentId || recommendation._id}`} className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                                        )}
                                                        {t('patientappointment.cancel_appointment_button')}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* View More Slots Section */}
                            {isViewingMoreSlots && selectedRecommendation && (
                                <div data-testid="reschedule-recommendation-view-more-section" className="mt-6 p-4 sm:p-6 bg-gradient-to-br from-yellow-50/50 to-orange-50/30 border border-yellow-200 rounded-xl">
                                    <h4 data-testid="reschedule-recommendation-view-more-title" className="text-lg font-semibold text-gray-800 mb-2">
                                        {t('patientappointment.view_more_slots_title') || 'Select an Available Slot'}
                                    </h4>
                                    <p data-testid="reschedule-recommendation-view-more-description" className="text-sm text-gray-600 mb-4">
                                        {t('patientappointment.view_more_slots_description') || 'Click on any available slot to reschedule your appointment'}
                                    </p>

                                    {appointmentLoading ? (
                                        <div data-testid="reschedule-recommendation-view-more-loading" className="flex justify-center items-center py-8">
                                            <span className="h-5 w-5 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                        </div>
                                    ) : (
                                        <div className="space-y-6">
                                            {/* Week Navigation - Always visible */}
                                            {(() => {
                                                const currentWeekMonday = getCurrentWeekMonday(today);
                                                const startDate = addDays(currentWeekMonday, weekOffsetMore * 7);
                                                const endDate = addDays(startDate, 6);
                                                return (
                                                    <div data-testid="reschedule-recommendation-view-more-week-nav" className="flex items-center justify-center gap-4 sm:gap-6 mb-4">
                                                        <button
                                                            data-testid="reschedule-recommendation-view-more-prev-week"
                                                            onClick={handlePrevWeekMore}
                                                            disabled={weekOffsetMore === 0}
                                                            className="p-2 bg-[#4ABDBD] text-white rounded-[16px] disabled:opacity-50 disabled:cursor-not-allowed shadow hover:bg-[#3AA8A8] transition-all duration-300 cursor-pointer"
                                                        >
                                                            <ArrowLeft size={20} />
                                                        </button>
                                                        <h3 data-testid="reschedule-recommendation-view-more-week-range" className="text-lg font-semibold text-gray-800">
                                                            {format(startDate, 'MMM d, yyyy')} - {format(endDate, 'MMM d, yyyy')}
                                                        </h3>
                                                        <button
                                                            data-testid="reschedule-recommendation-view-more-next-week"
                                                            onClick={handleNextWeekMore}
                                                            disabled={weekOffsetMore === 2}
                                                            className="p-2 bg-[#4ABDBD] text-white rounded-[16px] disabled:opacity-50 disabled:cursor-not-allowed shadow hover:bg-[#3AA8A8] transition-all duration-300 cursor-pointer"
                                                        >
                                                            <ArrowRight size={20} />
                                                        </button>
                                                    </div>
                                                );
                                            })()}

                                            {/* Available Days */}
                                            {availableSlots && Object.keys(availableSlots).length > 0 ? (
                                                (() => {
                                                    const availableDays = Object.entries(availableSlots || {})
                                                        .map(([day, data]) => ({ day, ...data }))
                                                        .sort((a, b) => new Date(a.date) - new Date(b.date));

                                                    return (
                                                        <div className="grid grid-cols-1 gap-4">
                                                            {availableDays.map(({ day, date, slots }) => {
                                                                const hasAvailableSlots = slots.filter(slot => slot.status === 'available').length > 0;
                                                                if (!hasAvailableSlots) return null;

                                                                return (
                                                                    <div
                                                                        data-testid={`reschedule-recommendation-day-card-${day}`}
                                                                        key={day}
                                                                        className="group overflow-hidden rounded-xl border border-gray-200 bg-gray-50 shadow-sm hover:shadow-[0_0px_8px_0_#4ABDBD] transition-all duration-300"
                                                                    >
                                                                        <div
                                                                            data-testid={`reschedule-recommendation-day-toggle-${day}`}
                                                                            className="p-4 bg-gray-50 flex items-center gap-3 cursor-pointer hover:bg-gray-100 transition-all duration-200"
                                                                            onClick={() => toggleDayDrawerMore(day)}
                                                                        >
                                                                            <CalendarCheck size={20} className="text-[#4ABDBD]" />
                                                                            <h4 data-testid={`reschedule-recommendation-day-label-${day}`} className="font-semibold text-gray-500 group-hover:text-[#4ABDBD] transition-colors truncate">
                                                                                {t(`patientappointment.days.${format(new Date(date), 'EEEE')}`)}, {format(new Date(date), 'MMM d, yyyy')}
                                                                            </h4>
                                                                        </div>
                                                                        {openDayMore === day && (
                                                                            <div data-testid={`reschedule-recommendation-day-slots-${day}`} className="p-4 bg-white border-t border-gray-200">
                                                                                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                                                                                    {slots
                                                                                        .filter(slot => slot.status === 'available')
                                                                                        .map((slot, idx) => (
                                                                                            <button
                                                                                                data-testid={`reschedule-recommendation-slot-${day}-${idx}`}
                                                                                                key={idx}
                                                                                                type="button"
                                                                                                onClick={() => handleDirectSlotClick(slot, selectedRecommendation)}
                                                                                                disabled={appointmentLoading}
                                                                                                className="p-2 rounded-xl text-center text-sm transition-all duration-200 bg-gray-50 border border-gray-200 text-gray-700 hover:bg-[#4ABDBD] hover:text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                            >
                                                                                                {format(new Date(slot.start), 'h:mm a')}
                                                                                            </button>
                                                                                        ))}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    );
                                                })()
                                            ) : (
                                                <div data-testid="reschedule-recommendation-view-more-empty" className="text-center py-8 text-gray-500">
                                                    {t('patientappointment.no_slots_available')}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div data-testid="reschedule-recommendation-modal-footer" className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                            <button
                                data-testid="reschedule-recommendation-modal-footer-close"
                                onClick={() => {
                                    setIsRecommendationModalOpen(false);
                                    setSelectedRecommendation(null);
                                    setIsViewingMoreSlots(false);
                                    setWeekOffsetMore(0);
                                    setOpenDayMore(null);
                                }}
                                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                            >
                                {t('patientappointment.close_button')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}